import { useEffect, useState } from "react";
import { Button } from "../../../components/button/button.jsx";
import { AdminHeader } from "../../../components/adminHeader/adminHeader.jsx";
import { useLocation, useParams } from "react-router";
import { ScrollableMenu } from "../../../components/scrollableMenu/scrollableMenu.jsx";
import { priorityRepeatTabs, workerRepeatTabs } from "./repeatTabs.js";
import { useAnimatedNavigate } from "../../../hooks/useAnimatedNavigate.js";
import { InputBlock } from "./InputBlock.jsx";
import { PageLoader } from "../../../components/loader/pageLoader.jsx";
import CheckListAPI from "../../../api/CheckListAPI.js";
import classNames from "classnames";
import qs from "qs";

import styles from "./workerTaskEditPage.module.css";
import { Retractable } from "../../../components/retractable/retractable.jsx";

import { Text } from "../../../components/text/text";

export const WorkerTaskEditPage = () => {
    const { userId, taskId } = useParams();
    const { search } = useLocation();
    const searchParams = qs.parse(search.replace("?", ""));

    const [tabId, setTabId] = useState(null);

    const [description, setDescription] = useState("");
    const [descriptionError, setDescriptionError] = useState(false);

    const [steps, setSteps] = useState(1);
    const [stepsError, setStepsError] = useState(false);

    const [exp, setExp] = useState(0);
    const [expError, setExpError] = useState(false);

    const [repeat, setRepeat] = useState(workerRepeatTabs[0]);
    const [repeatDays, setRepeatDays] = useState(1);
    const [repeatDaysError, setRepeatDaysError] = useState(false);

    const [beginDate, setBeginDate] = useState(new Date());
    const [beginDateError, setBeginDateError] = useState(false);

    const [endDate, setEndDate] = useState(new Date());
    const [endDateError, setEndDateError] = useState(false);

    const [countDone, setCountDone] = useState(0);
    const [taskType, setTaskType] = useState("");

    const [priority, setPriority] = useState(priorityRepeatTabs[0]);

    const resetErrors = () => {
        setDescriptionError(false);
        setStepsError(false);
        setExpError(false);
        setRepeatDaysError(false);
        setBeginDateError(false);
        setEndDateError(false);
    }

    const validateData = () => {
        let errors = false;

        if (description.length === 0) {
            setDescriptionError(true);
            errors = true;
        }

        if (steps === 0) {
            setStepsError(true);
            errors = true;
        }

        if (repeatDays === 0) {
            setRepeatDaysError(true);
            errors = true;
        }

        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (searchParams.type !== "reccurent") {
            if (beginDate.getTime() > endDate.getTime()) {
                setBeginDateError(true);
                setEndDateError(true);
                errors = true;
            } else if (beginDate.getTime() < now.getTime()) {
                setBeginDateError(true);
                errors = true;
            } else if (endDate.getTime() < now.getTime()) {
                setEndDateError(true);
                errors = true;
            }
        }

        return errors;
    }

    const { cafeId } = useParams();

    const [pageLoading, setPageLoading] = useState(true);
    const [checklistCategories, setChecklistCategories] = useState([]);
    useEffect(() => {
        CheckListAPI
            .fetchChecklistCategories(cafeId)
            .then((res) => {
                setChecklistCategories(res);
            })
            .catch(console.error);
    }, [cafeId]);

    useEffect(() => {
        if (checklistCategories.length === 0) return;

        if (searchParams.type === "reccurent") {
            CheckListAPI
                .fetchReccurentTaskData(userId, searchParams.taskId)
                .then(res => {
                    setDescription(res.taskText);
                    setSteps(res.countTotal);
                    setExp(res.rewardExp);
                    setPriority(priorityRepeatTabs[res.priority]);
                    setTabId(checklistCategories.find(el => el.text === res.categoryName)?.id);
                    const index = workerRepeatTabs.findIndex(el => el.value === res.repeatEveryNDays);
                    setRepeat(workerRepeatTabs.at(index));
                    if (index === -1) setRepeatDays(res.repeatEveryNDays);
                })
                .catch(console.error)
                .finally(() => setPageLoading(false));
        } else {
            CheckListAPI
                .fetchUserTaskData(userId, taskId)
                .then(res => {
                    setDescription(res.taskText);
                    setSteps(res.countTotal);
                    setExp(res.rewardExp);
                    setPriority(priorityRepeatTabs[res.priority]);
                    setTabId(checklistCategories.find(el => el.text === res.categoryName)?.id);
                    setCountDone(res.countDone);
                    setBeginDate(new Date(res.startDate ?? res.assigneeDate));
                    setEndDate(new Date(res.endDate ?? res.startDate ?? res.assigneeDate));
                    setTaskType(res.taskType);
                })
                .catch(console.error)
                .finally(() => setPageLoading(false));
        }
    }, [checklistCategories, userId, taskId, searchParams.taskId, searchParams.type]);

    const navigate = useAnimatedNavigate();
    const [loading, setLoading] = useState(false);
    const onSave = () => {
        resetErrors();
        if (!cafeId) return;
        if (validateData()) return;

        setLoading(true);
        if (searchParams.type !== "reccurent") {
            CheckListAPI
                .updateTask(
                    +taskId,
                    taskType,
                    description,
                    priority.value,
                    steps,
                    Math.min(countDone, steps),
                    exp,
                    checklistCategories.find(el => el.id === tabId).text,
                    beginDate,
                    endDate,
                )
                .then(() => {
                    const query = qs.stringify({
                        initialTab: tabId,
                        initialDaily: true,
                    });

                    navigate(`/admin/${cafeId}/cafe/employees/${userId}/tasks?${query}`);
                })
                .catch(console.error)
                .finally(() => setLoading(false));
        } else {
            CheckListAPI
                .updateReccurentTask(
                    searchParams.taskId,
                    description,
                    priority.value,
                    steps,
                    exp,
                    checklistCategories.find(el => el.id === tabId).text,
                    (repeat.ownVariant) ? repeatDays : repeat.value
                )
                .then(() => {
                    const query = qs.stringify({
                        initialTab: tabId,
                        initialDaily: true,
                    });

                    navigate(`/admin/${cafeId}/cafe/employees/${userId}/tasks?${query}`);
                })
                .catch(console.error)
                .finally(() => setLoading(false));
        }
    }

    if (pageLoading) {
        return <PageLoader />;
    }

    return (
        <>
            <AdminHeader title="Редактировать задачу" />

            <Text as="div" variant="body" className="page_description" >
                Выберите категорию и задайте параметры для редактируемой задачи.
            </Text>

            <ScrollableMenu
                buttons={checklistCategories}
                current={tabId}
                setCurrent={setTabId}
            />

            <InputBlock
                type="text"
                title="Кратко опишите задачу"
                value={description}
                setValue={setDescription}
                error={descriptionError}
            />

            <InputBlock
                type="number"
                title="Установите количество шагов в задаче, например если продать нужно 5 десертов, поставьте в поле цифру 5"
                value={steps}
                setValue={setSteps}
                error={stepsError}
            />

            <InputBlock
                type="number"
                title="Установите количество опыта, которое получит сотрудник при выполнении задачи"
                value={exp}
                setValue={setExp}
                error={expError}
            />

            <div className={styles.input_block}>
                <Text as="div" variant="h3" className={styles.input_block_title} size={12} weight={400} lineHeight="1">
                    Укажите приоритет задачи
                </Text>
                <div className={styles.repeat_tabs}>
                    {
                        priorityRepeatTabs.map(data => (
                            <div
                                key={data.id}
                                className={classNames(styles.repeat_tab, { [styles.active]: data.id === priority.id })}
                                onClick={() => setPriority(data)}
                            >
                                {data.text}
                            </div>
                        ))
                    }
                </div>
            </div>

            {
                searchParams.type !== "reccurent" ?
                    <div className={styles["date-time-block"]}>
                        <InputBlock
                            type="date"
                            className={styles.start_date_input}
                            title="Дата начала"
                            value={beginDate.toISOString().split("T")[0]}
                            setValue={(val) => {
                                try {
                                    const newDate = new Date(val.split("T")[0]);
                                    newDate.toISOString();
                                    setBeginDate(newDate);
                                } catch {
                                    setBeginDate(prev => prev);
                                }
                            }}
                            error={beginDateError}
                        />

                        <InputBlock
                            type="date"
                            className={styles.start_date_input}
                            title="Дата конца"
                            value={endDate.toISOString().split("T")[0]}
                            setValue={(val) => {
                                try {
                                    const newDate = new Date(val.split("T")[0]);
                                    newDate.toISOString();
                                    setEndDate(newDate);
                                } catch {
                                    setEndDate(prev => prev);
                                }
                            }}
                            error={endDateError}
                        />
                    </div> :
                    <div className={styles.input_block}>
                        <Text as="div" variant="h3" className={styles.input_block_title} size={12} weight={400} lineHeight="1">
                            Укажите период повторяющейся задачи
                        </Text>
                        <div className={styles.repeat_tabs}>
                            {
                                workerRepeatTabs.map(data => (
                                    <div
                                        key={data.id}
                                        className={classNames(styles.repeat_tab, { [styles.active]: data.id === repeat.id })}
                                        onClick={() => setRepeat(data)}
                                    >
                                        {data.text}
                                    </div>
                                ))
                            }
                        </div>
                        <Retractable active={repeat.ownVariant}>
                            <InputBlock
                                min={1}
                                className={styles.repeat_days_input}
                                type="number"
                                title="Кол-во дней повтора"
                                value={repeatDays}
                                setValue={setRepeatDays}
                                error={repeatDaysError}
                            />
                        </Retractable>
                    </div>
            }

            <Button text="Сохранить" type="primary" onClick={onSave} disabled={loading} />
        </>
    )
}
